import { useTranslation } from 'react-i18next';
import { FindingsPanel } from '@ideoz/ui';
import { PanelRightOpen, PanelLeftOpen } from 'lucide-react';
import { useConversation } from '../context/ConversationContext';
import type { ConversationFindings } from '../hooks/useConversationFindings';

interface ScopeIntelligenceRailProps {
  /** The single findings-state instance owned by ConversationModule. */
  findings: ConversationFindings;
  /** Hidden while the prototyper panel or a streamed-artifact side panel is open. */
  isPrototypeOpen: boolean;
  streamedArtifactOpen: boolean;
}

/**
 * The fixed right-hand Scope Intelligence rail (durable findings panel) + its
 * collapsed-state re-open FAB. Presentational: findings state comes in via the
 * `findings` prop (owned by ConversationModule), summary/verdict passthroughs
 * are read from the shared facade.
 */
export function ScopeIntelligenceRail({
  findings,
  isPrototypeOpen,
  streamedArtifactOpen,
}: ScopeIntelligenceRailProps): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const OpenIcon = i18n.dir() === 'rtl' ? PanelLeftOpen : PanelRightOpen;
  const { facade } = useConversation();
  const currentScopeId = facade.state.currentScopeId;
  const {
    panelFindings,
    resolvingFindingId,
    handleFindingAction,
    isFindingsOpen,
    isFindingsCollapsed,
    setIsFindingsCollapsed,
    includeDismissedFindings,
    setIncludeDismissedFindings,
  } = findings;

  if (!isFindingsOpen || isPrototypeOpen || streamedArtifactOpen) return null;

  // Collapsed → a FAB to re-open.
  if (isFindingsCollapsed) {
    return (
      <button
        type="button"
        onClick={() => setIsFindingsCollapsed(false)}
        aria-label={t('scopeIntel.open')}
        className="fixed end-5 top-5 z-40 flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-primary-600 shadow-md transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
      >
        <OpenIcon className="h-5 w-5" strokeWidth={2} />
      </button>
    );
  }

  return (
    <div className="flex flex-col fixed end-0 top-0 bottom-0 z-40 w-[var(--sidebar-width-findings)] min-h-0 p-5 border-s border-gray-200 bg-surface-2 dark:border-gray-800">
      <FindingsPanel
        findings={panelFindings}
        summary={facade.findings.summary}
        verdict={facade.findings.verdict}
        inTurnFlash={facade.findings.inTurnFlash}
        isLoading={facade.findings.isLoading}
        resolvingFindingId={resolvingFindingId}
        error={facade.findings.error}
        includeDismissed={includeDismissedFindings}
        onIncludeDismissedChange={(next) => {
          setIncludeDismissedFindings(next);
          if (currentScopeId) {
            void facade.findings.loadPanel(currentScopeId, next);
          }
        }}
        onAction={(findingId, action) => {
          void handleFindingAction(findingId, action);
        }}
        onCollapse={() => setIsFindingsCollapsed(true)}
      />
    </div>
  );
}

export default ScopeIntelligenceRail;
